import { Card, CardContent, CardHeader } from '@ui/card.tsx';
import { InfoIcon } from 'lucide-react';
import { Switch } from '@ui/switch.tsx';
import { useMutation } from '@tanstack/react-query';
import { Label } from '@ui/label.tsx';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  useApiAdminscheduledTasksGetCollection,
  useApiAdminuserScheduledTasksPost,
  apiAdminuserScheduledTasksIdDelete
} from '@r/api/generated/client.ts';
import type { UserScheduledTaskUserScheduledTaskWriteModel } from '@r/api/generated/client.schemas.ts';
import { useOrvalErrorToast, useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';

type TaskState = {
  [key: string]: boolean;
};

type TaskLoadingState = {
  [key: string]: boolean;
};

type ScheduledTasksRequestContext =
  | {
      id: number;
      previousState: boolean;
    }
  | undefined;

export default function ScheduledTasks() {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();

  const scheduledTasksQuery = useApiAdminscheduledTasksGetCollection();
  useOrvalQueryErrorToast(scheduledTasksQuery, t('toast.error.label'));

  const scheduledTasks = scheduledTasksQuery.data;
  const isScheduledTasksSuccess = scheduledTasksQuery.isSuccess;
  const scheduledTasksRefetch = scheduledTasksQuery.refetch;

  const [switchStates, setSwitchStates] = useState<TaskState>(() => {
    const initialStates: TaskState = {};

    for (const task of scheduledTasks || []) {
      initialStates[task.id] = false;
    }

    return initialStates;
  });

  const [loadingStates, setLoadingStates] = useState<TaskLoadingState>({});

  useEffect(() => {
    if (isScheduledTasksSuccess && scheduledTasks) {
      setSwitchStates((prevState) => {
        const newState = { ...prevState };

        for (const task of scheduledTasks) {
          if (task.currentUserScheduledTask) {
            newState[task.id] = true;
          }
        }

        return newState;
      });

      setLoadingStates({});
    }
  }, [isScheduledTasksSuccess, scheduledTasks]);

  const handleRequestOnMutate = (id: number) => {
    const previousState = switchStates[id];

    setLoadingStates((prevState) => ({ ...prevState, [id]: true }));
    setSwitchStates((prevState) => ({ ...prevState, [id]: !previousState }));

    return { id, previousState };
  };

  const handleRequestSuccess = async (message: string) => {
    await scheduledTasksRefetch();
    toast.success(message);
  };

  const handleRequestError = (
    error: unknown,
    context: ScheduledTasksRequestContext,
    message: string
  ) => {
    if (context?.id) {
      setLoadingStates((prevState) => ({ ...prevState, [context.id]: false }));
      setSwitchStates((prevState) => ({ ...prevState, [context.id]: context.previousState }));
    }
    showOrvalErrorToast(error, message);
  };

  const activateTask = useApiAdminuserScheduledTasksPost({
    mutation: {
      onMutate: (variables) => {
        const taskIdStr = variables.data.scheduledTask.split('/').pop();
        const taskId = taskIdStr ? Number.parseInt(taskIdStr, 10) : 0;
        return handleRequestOnMutate(taskId);
      },
      onSuccess: () => {
        handleRequestSuccess(t('admin.scheduled.task.activation.success'));
      },
      onError: (error, _variables, context) => {
        handleRequestError(error, context, t('admin.scheduled.task.activation.error'));
      },
      onSettled: (_data, _error, variables) => {
        const taskIdStr = variables.data.scheduledTask.split('/').pop();
        const taskId = taskIdStr ? Number.parseInt(taskIdStr, 10) : 0;
        setLoadingStates((prevState) => ({ ...prevState, [taskId]: false }));
      }
    }
  });

  const disableTask = useMutation({
    mutationFn: async (id: number) => {
      const userScheduledTaskId = scheduledTasks?.find((task) => task.id === id)
        ?.currentUserScheduledTask?.id;

      if (!userScheduledTaskId) {
        throw new Error('User scheduled task ID not found');
      }

      await apiAdminuserScheduledTasksIdDelete(String(userScheduledTaskId));
    },
    onMutate: (id: number) => {
      return handleRequestOnMutate(id);
    },
    onSuccess: () => {
      handleRequestSuccess(t('admin.scheduled.task.delete.success'));
    },
    onError: (error, _variables, context) => {
      handleRequestError(error, context, t('admin.scheduled.task.delete.error'));
    },
    onSettled: (_data, _error, variables) => {
      setLoadingStates((prevState) => ({ ...prevState, [variables]: false }));
    }
  });

  const handleSwitchClick = (id: number) => {
    if (switchStates[id]) {
      disableTask.mutate(id);
    } else {
      const payload: UserScheduledTaskUserScheduledTaskWriteModel = {
        scheduledTask: `/admin/scheduledTasks/${id}`
      };
      activateTask.mutate({ data: payload });
    }
  };

  return (
    <div className="space-y-10">
      <h1 className="text-4xl font-bold">{t('admin.scheduled.task.title')}</h1>

      <Card>
        <CardHeader title="Scheduled Actions" className="space-y-2.5">
          <h2 className="text-3xl font-bold">{t('admin.scheduled.task.subtitle')}</h2>
          <p className="text-muted-foreground">{t('admin.scheduled.task.description')}</p>

          <div className="flex items-center space-x-2.5">
            <InfoIcon className="stroke-info" size={20} />
            <p className="text-muted-foreground">{t('admin.scheduled.task.info')}</p>
          </div>
        </CardHeader>

        <CardContent>
          {isScheduledTasksSuccess && scheduledTasks && (
            <div className="space-y-7">
              {scheduledTasks?.map((task) => (
                <div key={task.id} className="flex items-center space-x-2">
                  <Switch
                    id={task.id.toString()}
                    checked={switchStates[task.id]}
                    onClick={() => handleSwitchClick(task.id)}
                    disabled={loadingStates[task.id]}
                  />

                  <Label className="mb-0" htmlFor={task.id.toString()}>
                    {t(task.description)}
                  </Label>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
